<template>
    <v-form ref="form" v-model="valid" data-cy="form">
        <v-row class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="4" class="align-center">
                <v-row>
                    <v-switch
                        v-model="flightNumber.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="flightNumber.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                        @update:model-value="resetClasses()"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="4">
                <TextFieldLocale
                    v-model="flightNumber.name[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                    :rules="[requiredRule('name')]"
                />
                <v-text-field
                    v-model="flightNumber.categoryDescription"
                    :label="$t('description_for_internal_use')"
                    hide-details
                    density="compact"
                    variant="outlined"
                    color="primary"
                    data-cy="category-description"
                    class="mb-4"
                />
            </v-col>
        </v-row>
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="4">
                <div class="text-subtitle-1">{{ $t('flights.service_classes') }}*</div>
                <div v-for="flight in flightNumber.apiOwnCharterServiceCategories" :key="flight.serviceCategoryClass">
                    <v-checkbox
                        v-model="flight.active"
                        :label="$t(`flights.serviceClass.${flight.serviceCategoryClass}`)"
                        density="compact"
                        color="primary"
                        hide-details
                        :data-cy="flight.serviceCategoryClass"
                        :readonly="!flightNumber.active"
                    />
                </div>
                <div
                    v-if="isShowServiceClassError && flightNumber.active"
                    class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular"
                >
                    {{ `${$t('flights.service_classes')} ${$t('validation.required')}` }}
                </div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <div class="text-subtitle-1">{{ $t('flights.direction') }}</div>
                <v-radio-group v-model="flightNumber.direction" density="compact">
                    <v-radio
                        :label="$t('flights.flightDirection.OUTBOUND')"
                        value="OUTBOUND"
                        color="primary"
                        data-cy="inbound"
                        prepend-inner-icon="mdi-calendar"
                    >
                        <template v-slot:label>
                            <v-icon start color="primary">mdi-airplane-takeoff</v-icon>
                            <span>
                                {{ $t('flights.flightDirection.OUTBOUND') }}
                            </span>
                        </template>
                    </v-radio>
                    <v-radio
                        :label="$t('flights.flightDirection.INBOUND')"
                        value="INBOUND"
                        color="primary"
                        data-cy="outbound"
                    >
                        <template v-slot:label>
                            <v-icon start color="primary">mdi-airplane-landing</v-icon>
                            <span>
                                {{ $t('flights.flightDirection.INBOUND') }}
                            </span>
                        </template>
                    </v-radio>
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn data-cy="save" :disabled="isLoading || !valid" @click="checkValidation">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import type {FlightNumberType} from '~/utils/types/account/products/types'
    import {initFlightNumber} from '~/utils/types/account/products/helpers'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {ServiceClass} from '~/utils/types/types'
    import {clone} from '~/utils/helpers'

    const props = defineProps<{
        editFlightNumberId?: number | null
        contractId: number
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const flightNumber = ref<FlightNumberType>(initFlightNumber())
    const isShowServiceClassError = ref<boolean>(false)

    onMounted(async () => {
        if (props.editFlightNumberId) await loadData()
        if (!props.editFlightNumberId) {
            flightNumber.value.apiOwnCharterServiceCategories = useApiOwnCharterServiceCategories.value
        }
        activeLangCode.value = app.$i18n.locale.value
    })

    enum ServiceCategoryClass {
        ECONOM = 0,
        BUSINESS = 1,
        FIRST = 2,
        PREMIUM_ECONOMY = 3,
    }

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const res = await app.$api.ownCharterServiceCategories.get({id: props.editFlightNumberId, locale: 'all'})
            flightNumber.value = res[0]
            flightNumber.value.apiOwnCharterServiceCategories.sort(
                (a, b) => ServiceCategoryClass[a.serviceCategoryClass] - ServiceCategoryClass[b.serviceCategoryClass]
            )
        } catch (e) {
            flightNumber.value = initFlightNumber()
            flightNumber.value.apiOwnCharterServiceCategories = useApiOwnCharterServiceCategories.value
        } finally {
            isLoading.value = false
        }
    }

    const resetClasses = () => {
        if (!flightNumber.value.active) {
            flightNumber.value.apiOwnCharterServiceCategories = flightNumber.value.apiOwnCharterServiceCategories.map(
                item => ({...item, active: false})
            )
        }
    }

    const useApiOwnCharterServiceCategories = computed(() => {
        const serviceClasses = [
            ServiceClass.Econom,
            ServiceClass.Business,
            ServiceClass.First,
            ServiceClass.PremiumEconomy,
        ]
        return serviceClasses.map(item => ({
            serviceCategoryClass: item,
            active: false,
        }))
    })

    const checkValidation = () => {
        if (!flightNumber.value.active) {
            save()
        } else {
            const valueToCheck = true
            const valid = flightNumber.value.apiOwnCharterServiceCategories.find(item => item.active === valueToCheck)
            valid ? save() : (isShowServiceClassError.value = true)
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(flightNumber.value)
            clean(data)
            if (props.editFlightNumberId) {
                await app.$api.ownCharterServiceCategories.put({
                    id: props.editFlightNumberId,
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownCharterServiceCategories.post({
                    contractId: props.contractId,
                    ...data,
                })
            }

            await supplierStore.loadCharterServiceCategories()
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }
</script>
